import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, Phone, Package } from 'lucide-react';
import { trackPageView, trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DealerLocatorPage = () => {
  const [pincode, setPincode] = useState('');
  const [district, setDistrict] = useState('');
  const [dealers, setDealers] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { trackPageView('/dealer-locator', 'Dealer Locator'); }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (pincode) params.set('pincode', pincode);
      if (district) params.set('district', district);
      const res = await fetch(`${BACKEND_URL}/api/dealers/locate?${params}`);
      const data = await res.json();
      setDealers(data);
      trackEvent('dealer_search', 'locator', { pincode, district, results: data.total });
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="dealer-locator-page">
      <SEOHead
        title="Find a Dealer"
        description="Locate authorized Avi Cement dealers near you. Search by district or pincode across Gujarat and Rajasthan. 750+ dealers serving your area."
        path="/dealer-locator"
      />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center mb-10">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Dealer Network</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Nearest <span className="text-[#F5A623]">Dealer</span> Dhoondhein
        </h1>
        <p className="text-gray-400 text-sm max-w-md mx-auto">Pincode ya district daalein — 750+ dealers mein se sabse nazdeeki dealer ki info milegi.</p>
      </motion.div>

      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        onSubmit={handleSearch} className="max-w-2xl mx-auto mb-10" data-testid="dealer-locator-form">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" value={pincode} onChange={e => setPincode(e.target.value)} placeholder="Pincode (e.g. 380013)"
              data-testid="dealer-locator-pincode"
              className="w-full pl-11 pr-4 py-4 rounded-xl bg-[#111113] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
          </div>
          <span className="text-gray-600 text-sm self-center">ya</span>
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" value={district} onChange={e => setDistrict(e.target.value)} placeholder="District (e.g. Ahmedabad)"
              data-testid="dealer-locator-district"
              className="w-full pl-11 pr-4 py-4 rounded-xl bg-[#111113] border border-gray-800 text-white text-sm focus:outline-none focus:border-[#E85A1C]" />
          </div>
          <button type="submit" disabled={loading} data-testid="dealer-locator-search-btn"
            className="px-8 py-4 rounded-xl bg-[#E85A1C] hover:bg-[#d14e15] text-white font-medium text-sm transition-all disabled:opacity-50 whitespace-nowrap">
            {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" /> : 'Search'}
          </button>
        </div>
      </motion.form>

      {dealers && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto" data-testid="dealer-locator-results">
          <p className="text-gray-400 text-sm mb-4">{dealers.total} dealer{dealers.total !== 1 ? 's' : ''} found</p>
          {dealers.total === 0 ? (
            <div className="text-center py-12 bg-[#111113] rounded-2xl border border-gray-800">
              <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-white text-lg font-medium mb-2">Koi dealer nahi mila</p>
              <p className="text-gray-500 text-sm">Doosra pincode ya district try karein, ya hamse contact karein.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dealers.dealers.map((d, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className="bg-[#111113] border border-gray-800 rounded-xl p-5 hover:border-[#E85A1C]/30 transition-all" data-testid={`dealer-card-${idx}`}>
                  <h3 className="text-white font-semibold text-sm mb-1">{d.name}</h3>
                  <p className="text-gray-500 text-xs mb-3">{d.area}, {d.district} — {d.pincode}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <Phone className="w-3.5 h-3.5 text-[#E85A1C]" />
                    <a href={`tel:${d.phone}`} className="text-gray-300 text-sm hover:text-white transition-colors">{d.contact} — {d.phone}</a>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {d.products.map(p => (
                      <span key={p} className="px-2 py-0.5 rounded text-xs bg-[#E85A1C]/10 text-[#E85A1C] border border-[#E85A1C]/20">{p}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default DealerLocatorPage;
